import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import Link from 'components/commercetools-ui/atoms/link';
import Overlay from 'components/commercetools-ui/atoms/overlay';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import Image from 'components/commercetools-ui/atoms/image';
import { AddToCartOverlayContextShape, StateProduct, VariantData } from './types';
import Button from 'components/commercetools-ui/atoms/button';
import { Product, Variant } from 'shared/types/product';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import configLineItemExtractor from 'helpers/configLineItemExtractor/configLineItemExtractor';
import useI18n from 'helpers/hooks/useI18n';

const AddToCartOverlayContext = React.createContext<AddToCartOverlayContextShape>({
  show() {},
  hide() {},
});

const AddToCartOverlayProvider = ({ children }: React.PropsWithChildren) => {
  const { locale } = useI18n();

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const { blockScroll } = useScrollBlock();

  const [product, setProduct] = useState<StateProduct>();
  const [variantData, setVariantData] = useState<VariantData>();

  const show = useCallback(
    (product: Product, variant: Variant, count: number, data?: VariantData) => {
      setProduct({ ...product, ...variant, count });
      setVariantData(data);
      blockScroll(true);
    },
    [blockScroll],
  );

  const hide = useCallback(() => {
    setProduct(undefined);
    setVariantData(undefined);
    blockScroll(false);
  }, [blockScroll]);

  const configDetails = useMemo(() => {
    if (!product || !variantData?.config) return '';

    const lineItemLike: ExtendedLineItem = {
      config: variantData.config,
    } as ExtendedLineItem;

    return configLineItemExtractor(lineItemLike);
  }, [product, variantData]);

  return (
    <>
      {product && <Overlay onClick={hide} zIndex={999} />}
      <Transition
        show={!!product}
        /* @ts-ignore it actually works and it applies the className */
        className="fixed bottom-0 z-[9999] w-full overflow-hidden rounded-[20px_20px_0_0] bg-white shadow md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-none"
        enter="transition md:transition-opacity duration-75"
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leave="transition md:transition-opacity duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div onMouseUp={(e) => e.stopPropagation()}>
          <div className="bg-primary p-24 md:p-32">
            <h4 className="body-text-large font-medium text-primary">
              {formatProductMessage({ id: 'cart.added', defaultMessage: 'Added to cart' })}
            </h4>

            <CloseIcon
              className="absolute right-20 top-22 h-24 w-24 cursor-pointer fill-primary-black stroke-0 md:right-28 md:top-30"
              onClick={hide}
            />

            <div className="mt-24 md:mt-34">
              <div className="flex items-center gap-16 md:items-start">
                <div className="shrink-0">
                  <Image
                    width={90}
                    height={90}
                    src={product?.images?.[0] ?? '#'}
                    style={{ objectFit: 'contain' }}
                    alt={product?.name ?? ''}
                  />
                </div>
                <div className="flex grow items-start justify-between overflow-hidden">
                  <div className="max-w-full overflow-hidden">
                    <p className="body-text block max-w-full pt-4 font-medium leading-6">{product?.name}</p>
                    {!!configDetails && <p className="body-text-x-small">{configDetails}</p>}
                    <p className="body-text block font-bold leading-6 md:hidden">
                      {CurrencyHelpers.formatForCurrency(
                        variantData?.price || product?.discountedPrice || product?.price || {},
                        locale,
                      )}
                    </p>
                    <p className="body-text-small block lowercase leading-[21px]">
                      x {product?.count} ({formatProductMessage({ id: 'quantity', defaultMessage: 'quantity' })})
                    </p>
                  </div>
                  <p className="body-text hidden font-bold leading-6 md:block">
                    {CurrencyHelpers.formatForCurrency(
                      variantData?.price || product?.discountedPrice || product?.price || {},
                      locale,
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-24 flex w-full flex-col gap-16 md:mt-32 md:flex-row-reverse">
              <Link link="/cart" onClick={hide} className="md:flex-1">
                <Button variant="primary" size="s" className="w-full">
                  {formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to cart' })}
                </Button>
              </Link>
              <div onClick={hide} className="md:flex-1">
                <Button variant="secondary" size="s" className="w-full">
                  {formatCartMessage({ id: 'continue.shopping', defaultMessage: 'Continue shopping' })}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <AddToCartOverlayContext.Provider value={{ show, hide }}>{children}</AddToCartOverlayContext.Provider>
    </>
  );
};

export default AddToCartOverlayProvider;

export const useAddToCartOverlay = () => useContext(AddToCartOverlayContext);
